<script setup>
import BasePreferenceTemplate from '@/components/preference/templates/BasePreferenceTemplate.vue'
import ModalEmailEdit from '@/components/edit/ModalEmailEdit.vue'
import PreferenceLogo from '@/components/preference/PreferenceLogo.vue'
import AutoSaveStatus from '@/components/preference/elements/AutoSaveStatus.vue'

import { withProps } from '@/components/preference/templates/preferenceTemplate.js'

defineProps({ ...withProps() })
defineEmits(['save', 'userEmail', 'update'])
defineOptions({ inheritAttrs: false })
</script>

<template>
  <BasePreferenceTemplate
    v-bind="{ ...$props, ...$attrs }"
    outer-class="bg-gray-50"
    container-class="min-h-screen max-h-[3840px] flex flex-col items-center justify-center py-12 px-4"
    :card-component="'div'"
    card-class="landing-card w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden"
    content-class=""
    @save="$emit('save')"
    @user-email="$emit('userEmail', $event)"
    @update="$emit('update')"
  >
    <template #form-step="{ logo, attrs, name, chosenEmail, updateChosenEmail, alertMsg, setEmail }">
      <div
        class="landing-header px-8 pt-8 pb-6 text-center"
        :style="{ background: attrs?.primary_color ? `linear-gradient(135deg, ${attrs.primary_color}, ${attrs.primary_color}cc)` : undefined }"
      >
        <div class="landing-logo-ring inline-flex items-center justify-center mb-3">
          <PreferenceLogo :url="logo" />
        </div>
        <h1 class="text-xl font-bold text-white mt-2">{{ attrs?.tag_subject || $t('pref_page.title') || 'Update your preferences' }}</h1>
        <p class="text-white/80 text-sm mt-1">{{ attrs?.tag_pre_name || $t('pref_page.header') || 'for ' }}{{ name }}</p>
      </div>

      <div class="px-8 py-6">
        <form class="flex flex-col space-y-4" @submit="setEmail($event, chosenEmail)">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 mb-1.5">{{ $t('pref_page_email_form.form_title') }}</label>
            <input
              id="email"
              :value="chosenEmail"
              type="email"
              name="email"
              class="block w-full rounded-lg border border-gray-200 py-2.5 px-3 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm transition-shadow"
              :placeholder="$t('pref_page_email_form.form_placeholder').replace('[at]', '@')"
              @input="updateChosenEmail($event.target.value)"
            />
            <span v-if="alertMsg" class="text-red-500 text-xs mt-1.5 block">{{ alertMsg }}</span>
          </div>
          <button
            type="submit"
            class="landing-btn w-full rounded-lg py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg"
            :style="attrs?.primary_color ? { background: `linear-gradient(135deg, ${attrs.primary_color}, ${attrs.primary_color}cc)` } : {}"
          >
            {{ $t('pref_page_email_form.form_save_btn') }}
          </button>
        </form>
      </div>
    </template>

    <template #preferences-header="{ logo, attrs, name }">
      <div
        class="landing-header px-8 pt-8 pb-6 text-center -mt-6"
        :style="{ background: attrs?.primary_color ? `linear-gradient(135deg, ${attrs.primary_color}, ${attrs.primary_color}cc)` : undefined }"
      >
        <div class="landing-logo-ring inline-flex items-center justify-center mb-3">
          <PreferenceLogo :url="logo" />
        </div>
        <h2 class="text-xl font-bold text-white mt-2">{{ attrs?.tag_subject || $t('pref_page.title') || 'Update your preferences' }}</h2>
        <p class="text-white/80 text-sm mt-1">{{ attrs?.tag_pre_name || $t('pref_page.header') || 'for ' }}{{ name }}</p>
      </div>
    </template>

    <template #save-feedback="{ saved, saveStatus, attrs }">
      <AutoSaveStatus v-if="attrs?.auto_save" :status="saveStatus" :color="attrs?.primary_color" />
      <Transition v-else name="slide-fade">
        <div v-if="saved" class="mx-6 p-3 text-center bg-green-50 text-green-700 rounded-lg text-sm border border-green-100">
          {{ attrs?.saved_preferences_msg || $t('pref_page.save_msg') || 'Your preferences have been saved' }}
        </div>
      </Transition>
    </template>

    <template #action-buttons="{ attrs, disabled, updateGlobal, savePreferences }">
      <div class="flex justify-center space-x-3 px-6 pb-6 pt-2">
        <button
          v-if="attrs.unsubscribe_all"
          class="w-full rounded-lg py-2.5 text-sm font-semibold text-gray-700 border border-gray-200 bg-white hover:bg-gray-50 transition-all shadow-sm"
          @click="!disabled ? updateGlobal() : null"
        >
          {{ $t('pref_page.unsubscribe_btn') }}
        </button>
        <button
          v-if="!attrs?.auto_save"
          class="landing-btn w-full rounded-lg py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg"
          :style="attrs?.primary_color ? { background: `linear-gradient(135deg, ${attrs.primary_color}, ${attrs.primary_color}cc)` } : {}"
          @click="savePreferences"
        >
          {{ $t('pref_page.save_btn') }}
        </button>
      </div>
    </template>
    <template #modal="{ modal, modalType, chosenEmail, customerId, preview, updateEmail, closeModal }">
      <Transition name="landing-modal">
        <div v-if="modal && modalType === 'email'" class="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div class="fixed inset-0 bg-black/30 backdrop-blur-sm" @click="closeModal" />
          <div class="landing-modal-panel relative w-full max-w-lg rounded-2xl bg-white shadow-xl overflow-hidden">
            <div
              class="landing-header px-6 py-4 flex items-center justify-between"
              :style="{ background: attrs?.primary_color ? `linear-gradient(135deg, ${attrs.primary_color}, ${attrs.primary_color}cc)` : undefined }"
            >
              <h3 class="text-lg font-semibold text-white">{{ $t('modal_mail.main_title') }}</h3>
              <button class="text-white/70 hover:text-white transition-colors" @click="closeModal">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
                  <path
                    fill-rule="evenodd"
                    d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </div>
            <div class="p-6">
              <ModalEmailEdit :email="chosenEmail" :customer-id="customerId" :attributes="attrs" :preview="preview" @save="updateEmail" @close="closeModal" />
            </div>
          </div>
        </div>
      </Transition>
    </template>
  </BasePreferenceTemplate>
</template>

<style scoped>
.landing-header {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.landing-header :deep(img) {
  max-width: 52px;
  max-height: 52px;
  border-radius: 14px;
}

.landing-header :deep(.mx-4) {
  margin: 0;
}

.landing-logo-ring {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 14px;
  padding: 4px;
}

.landing-btn {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.landing-card {
  box-shadow:
    0 25px 60px -12px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.3s ease;
}

/* --- Landing modal --- */
.landing-modal-panel {
  z-index: 10;
  box-shadow:
    0 25px 60px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(0, 0, 0, 0.04);
}

.landing-modal-panel :deep(div.px-4) {
  padding-left: 0;
  padding-right: 0;
}

.landing-modal-panel :deep(.border.w-fit) {
  display: none;
}

.landing-modal-panel :deep(.text-xl.font-medium) {
  display: none;
}

.landing-modal-panel :deep(.pb-4) {
  padding-bottom: 0;
}

.landing-modal-panel :deep(input) {
  border-radius: 0.5rem;
  border-color: #e5e7eb;
  transition: box-shadow 0.2s ease;
}

.landing-modal-panel :deep(input:focus) {
  border-color: transparent;
  box-shadow: 0 0 0 2px #3b82f6;
  outline: none;
}

.landing-modal-panel :deep(.flex.text-right button:last-child) {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px -2px rgba(59, 130, 246, 0.4);
}

.landing-modal-panel :deep(.flex.text-right button:first-child) {
  border-radius: 0.5rem;
}

.landing-modal-enter-active {
  transition: opacity 0.3s ease-out;
}

.landing-modal-leave-active {
  transition: opacity 0.2s ease-in;
}

.landing-modal-enter-from,
.landing-modal-leave-to {
  opacity: 0;
}
</style>
